import { ApiError, OfflineError } from '../../data/api'
import {
  isCoarseRoute,
  isDrive,
  routeState,
  stageRouteFrom,
  viaCoords,
  type RouteAnswer,
} from '../../lib/route'
import type { LatLon, Stage, StageRoute } from '../../lib/types'

/**
 * Der Sammelaufruf: alle fehlenden Strecken holen, eine nach der anderen.
 *
 * Bewusst kein React in dieser Datei. Der Ablauf - Reihenfolge, Wartezeit,
 * Abbruch, was ein Fehler bedeutet - ist die eigentliche Entscheidung und lässt
 * sich so ohne DOM prüfen. Die Oberfläche daneben hält nur den Fortschritt
 * und die Knöpfe.
 */

/**
 * Mindestabstand zwischen zwei Anfragen an den Routendienst.
 *
 * Der Server drosselt auf eine Anfrage je Sekunde. Etwas mehr als eine Sekunde,
 * damit die Drosselung nicht wegen einer Millisekunde zuschlägt: sechzehn
 * Etappen dauern damit knapp zwanzig Sekunden, und das ist die Sache wert.
 */
export const REQUEST_GAP_MS = 1100

/**
 * Wartezeit vor dem zweiten Versuch, wenn der Server die Drosselung ziehen
 * musste.
 *
 * Der Server lehnt eine zu schnelle Anfrage mit upstream_unavailable und
 * Retry-After: 1 ab - von aussen sieht das genauso aus wie ein Dienst, der
 * nicht antwortet. Ein einziger zweiter Versuch nach zwei Sekunden trennt die
 * beiden Fälle: war es nur die Drosselung, weil noch jemand anders gerade
 * rechnen liess, geht der Lauf weiter. Ist der Dienst wirklich weg, scheitert
 * auch der zweite Versuch und der Lauf endet - dann sind fünfzehn weitere
 * Anfragen niemandem eine Hilfe.
 */
export const RETRY_AFTER_MS = 2000

/** Wie weit der Sammelaufruf ist. */
export interface RouteProgress {
  /** Wie viele Etappen der Lauf insgesamt vorhat. */
  total: number
  /** Berechnete und gespeicherte Etappen. */
  done: number
  /** Etappen, für die der Dienst nichts liefern konnte. */
  failed: number
  /** Beschriftung der Etappe, die gerade läuft. */
  current: string
}

/** Wie der Lauf ausgegangen ist. */
export interface RouteRunResult {
  done: number
  failed: number
  /** Der Grund, an dem der Lauf hängengeblieben ist, sonst null. */
  error: string | null
  /** Wurde von Hand abgebrochen? */
  aborted: boolean
}

/** Was der Lauf von aussen braucht. Alles einsetzbar, damit er prüfbar bleibt. */
export interface RouteRunDeps {
  /** Holt eine Strecke, üblicherweise ApiClient.fetchRoute. */
  fetchRoute: (
    from: LatLon,
    via: LatLon[],
    to: LatLon,
    signal?: AbortSignal,
  ) => Promise<RouteAnswer>
  /** Legt das Ergebnis in der Etappe ab. */
  save: (stageId: string, route: StageRoute) => void
  onProgress?: (progress: RouteProgress) => void
  /** Abstand zwischen zwei Anfragen. */
  gapMs?: number
  /** Wartezeit vor dem zweiten Versuch nach einer Absage des Dienstes. */
  retryMs?: number
  sleep?: (ms: number, signal?: AbortSignal) => Promise<void>
  now?: () => number
  signal?: AbortSignal
}

/**
 * Die Etappen, für die der Sammelaufruf etwas zu tun hat.
 *
 * Ausgelassene Etappen bleiben liegen: sie werden nicht gefahren, und eine
 * Anfrage für sie wäre eine von sechzehn, die niemand braucht. Standtage
 * ebenso - dort ist das Ziel der Start. Veraltete Strecken gehören dagegen
 * dazu: sie sind der Grund, warum es den Knopf gibt.
 *
 * Grob ausgedünnte Strecken kommen mit (isCoarseRoute): sie stammen aus der
 * Zeit vor dem Verfahren, das nach Bedeutung auswählt, und schneiden an Pässen
 * die Kehren ab. Nichts geht dabei verloren, wenn der Lauf nicht durchkommt -
 * die grobe Linie bleibt gezeichnet, bis eine genauere da ist. Von selbst
 * losrennen darf deswegen niemand: jede dieser Etappen kostet eine Anfrage an
 * einen fremden Dienst, der eine je Sekunde erlaubt. Es bleibt beim Knopf.
 */
export function stagesNeedingRoute(stages: Stage[]): Stage[] {
  return stages.filter(
    (s) => !s.skip && isDrive(s) && (routeState(s) !== 'current' || isCoarseRoute(s)),
  )
}

/** Beschriftung einer Etappe für die Fortschrittsanzeige. */
export function stageLabel(stage: Stage): string {
  return `${stage.from} → ${stage.to}`
}

/**
 * Sagt, ob ein Fehler den ganzen Lauf beendet.
 *
 * Kein Netz, keine gültige Anmeldung oder ein Dienst, der nicht antwortet:
 * dann scheitern die fünfzehn folgenden Anfragen genauso, und fünfzehn
 * Sekunden Warten mit demselben Ergebnis sind niemandem eine Hilfe. Findet der
 * Dienst dagegen für eine einzelne Etappe keine Strasse, ist das ihr Problem
 * und nicht das des Laufs - der geht weiter.
 */
export function isFatal(error: unknown): boolean {
  if (error instanceof OfflineError) return true
  if (error instanceof ApiError) {
    return error.isAuthProblem || error.code === 'upstream_unavailable'
  }
  return false
}

/**
 * Der Fehler in einem Satz, den man dem Nutzer zeigen kann.
 *
 * Die Codes stammen aus der Zusage des Servers. Was dort nicht aufgeführt ist,
 * bekommt eine allgemeine Meldung samt Statuscode - erfundene Deutungen sind
 * schlimmer als ein zugegebenes "unbekannt".
 */
export function routeErrorText(error: unknown): string {
  if (error instanceof OfflineError) {
    return 'Kein Netz. Die Strecke lässt sich nur mit Verbindung berechnen.'
  }
  if (error instanceof ApiError) {
    if (error.isAuthProblem) return 'Die Anmeldung ist abgelaufen. Neu anmelden und noch einmal.'
    if (error.code === 'route_unavailable') {
      return 'Der Dienst findet keine Strasse zwischen diesen beiden Punkten.'
    }
    if (error.code === 'upstream_unavailable') {
      return 'Der Routendienst antwortet nicht. Später noch einmal versuchen.'
    }
    if (error.code === 'bad_request') {
      return 'Mit diesen Koordinaten kann der Dienst nichts anfangen.'
    }
    return `Der Server antwortete mit ${String(error.status)} (${error.code}).`
  }
  return 'Die Strecke liess sich nicht berechnen.'
}

/**
 * Holt die Strecken der übergebenen Etappen, eine nach der anderen.
 *
 * Der Reihe nach und nicht parallel: der Server drosselt, und sechzehn
 * gleichzeitige Anfragen bekämen fünfzehn Abweisungen. Zwischen zwei Anfragen
 * liegt gapMs, gewartet wird vor der Anfrage und nicht danach - so geht die
 * erste sofort los, und der Abstand gilt trotzdem für jedes Paar.
 *
 * Gespeichert wird nach jeder einzelnen Etappe. Bei einem Abbruch mitten im
 * Lauf ist damit alles behalten, was schon berechnet wurde; eine Sammelablage
 * am Ende wäre bei sechzehn Anfragen ein Alles-oder-nichts.
 */
export async function computeRoutes(stages: Stage[], deps: RouteRunDeps): Promise<RouteRunResult> {
  const gap = deps.gapMs ?? REQUEST_GAP_MS
  const sleep = deps.sleep ?? pause
  const now = deps.now ?? Date.now
  const total = stages.length

  let done = 0
  let failed = 0

  const melden = (current: string) => {
    deps.onProgress?.({ total, done, failed, current })
  }

  for (const [index, stage] of stages.entries()) {
    if (deps.signal?.aborted) return { done, failed, error: null, aborted: true }
    melden(stageLabel(stage))

    if (index > 0) {
      await sleep(gap, deps.signal)
      if (deps.signal?.aborted) return { done, failed, error: null, aborted: true }
    }

    try {
      const answer = await holeStrecke(stage, deps, sleep)
      const route = stageRouteFrom(answer, stage, now())
      if (route) {
        deps.save(stage.id, route)
        done++
      } else {
        // Eine Antwort ohne brauchbare Geometrie ist keine Strecke. Sie zählt
        // als Fehlschlag dieser Etappe, nicht des Laufs.
        failed++
      }
    } catch (error) {
      // Ein Abbruch lässt fetch werfen. Das ist kein Fehler, sondern der
      // Wunsch des Nutzers, und darf nicht als Fehlschlag gezählt werden.
      if (deps.signal?.aborted) return { done, failed, error: null, aborted: true }
      if (isFatal(error)) {
        return { done, failed, error: routeErrorText(error), aborted: false }
      }
      failed++
    }
  }

  melden('')
  return { done, failed, error: null, aborted: false }
}

/**
 * Holt die Strecke einer Etappe, mit einem zweiten Versuch nach einer Absage.
 *
 * Genau ein zweiter Versuch und keine Kette: eine Wiederholungsschleife würde
 * bei einem Dienst, der weg ist, aus sechzehn Anfragen zweiunddreissig machen
 * und den Nutzer minutenlang warten lassen.
 */
async function holeStrecke(
  stage: Stage,
  deps: RouteRunDeps,
  sleep: (ms: number, signal?: AbortSignal) => Promise<void>,
): Promise<RouteAnswer> {
  const via = viaCoords(stage)
  try {
    return await deps.fetchRoute(stage.c1, via, stage.c2, deps.signal)
  } catch (error) {
    if (deps.signal?.aborted || !isThrottled(error)) throw error
    await sleep(deps.retryMs ?? RETRY_AFTER_MS, deps.signal)
    if (deps.signal?.aborted) throw error
    return deps.fetchRoute(stage.c1, via, stage.c2, deps.signal)
  }
}

/** Die Absage, hinter der eine Drosselung stecken kann. */
function isThrottled(error: unknown): boolean {
  return error instanceof ApiError && error.status === 503 && error.code === 'upstream_unavailable'
}

/**
 * Wartet, lässt sich aber abbrechen.
 *
 * Ohne den Abbruch bliebe der Lauf nach einem Klick auf "Abbrechen" noch eine
 * Sekunde stehen, bevor er es merkt. Aufgelöst wird auch beim Abbruch, denn
 * der Aufrufer prüft das Abbruchzeichen ohnehin selbst - eine Ablehnung wäre
 * hier nur eine Ausnahme, die niemand behandeln will.
 */
function pause(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal?.aborted) {
      resolve()
      return
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', abbrechen)
      resolve()
    }, ms)
    function abbrechen() {
      clearTimeout(timer)
      resolve()
    }
    signal?.addEventListener('abort', abbrechen, { once: true })
  })
}
